import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { devLog } from '../../../../utils/devLog';
import { AlertTriangle, Clock, Trash2, Loader2, Camera } from 'lucide-react';
import { getJobDescription } from '../../../../config/maintenanceJobDescriptions';
import { formatDate, getStatusDisplay, filterJobHistory, getDefaultImage } from './viewHelpers';
import MaintenanceCheckForm from './MaintenanceCheckForm';
import QuickAddEquipmentModal from './QuickAddEquipmentModal';
import MaintenanceHistoryTable from './MaintenanceHistoryTable';
import JobDescriptionCard from './JobDescriptionCard';
import MaintenanceCheckHeader from './MaintenanceCheckHeader';
import EquipmentStatusTable from './EquipmentStatusTable';
import PhotoAttachment from '../../../common/PhotoAttachment';
import { uuidv4 } from '../../../common/PhotoAttachment/pipeline/uuid';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
// FT-349c: draft persistence + orphan cleanup on Cancel confirm.
// FT-351: view-state constants — Resume respects which sub-view the user
// was on when autosave last fired (photos vs form-open).
import {
  useMaintenanceDraft,
  DRAFT_STALE_MS,
  VIEW_STATE_FORM_OPEN,
  VIEW_STATE_PHOTOS,
} from '../../../../hooks/domain/maintenance/useMaintenanceDraft';
import { usePhotoStore } from '../../../common/PhotoAttachment/hooks/usePhotoStore';

const resolveJobEquipment = (job, equipmentList = []) => {
  const ids = Array.isArray(job?.linkedEquipmentIds) ? job.linkedEquipmentIds : [];
  if (ids.length === 0) return [];
  const idSet = new Set(ids);
  return equipmentList.filter(eq => eq && idSet.has(eq.id));
};

const MaintenanceCheckView = ({
  job,
  onBack,
  onSave,
  equipment = [],
  onAddEquipment,
  maintenanceHistory = [],
  crewList = [],
  onDelete,
  onEdit,  // FT-186: Edit callback for custom jobs
  isCompleting = false,
  isDeleting = false,
  canSignOff = false,
  onSignOff,
  // FT-346: <PhotoAttachment> wiring (all optional so tests / other callers
  // without vessel context still render).
  // FT-349b: `onUpdateJobPhotos` retained for backwards compatibility with the
  // FT-346 test harness only — photos now live in local state (per-completion).
  currentVesselId = null,
  capturedBy = 'unknown',
  onUpdateJobPhotos = null
}) => {
  // Add swipe-to-go-back functionality
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  const [jobDescription, setJobDescription] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [inspectorError, setInspectorError] = useState('');

  // Check if this is a custom job that can be deleted
  const isCustomJob = job?.custom === true ||
                      job?.type === 'custom' ||
                      job?.id?.includes(':custom:') ||
                      job?.category === 'custom';

  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  // FT-349b: per-completion state — completionId (stable id shared between
  // history record + job.completions[] entry) + photos (evidence for this
  // completion, threaded through in the onSave payload).
  const [completionId, setCompletionId] = useState(() => uuidv4());
  const [photos, setPhotos] = useState([]);
  const [signOffNow, setSignOffNow] = useState(false);

  // FT-349c: draft persistence + cancel-confirm modals.
  const { deleteByRecord } = usePhotoStore(currentVesselId);
  const {
    draft,
    loading: draftLoading,
    saveDraft,
    purgeDraft,
    discardDraft,
  } = useMaintenanceDraft(job?.id, currentVesselId, { deleteByRecord });
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [resumePromptHandled, setResumePromptHandled] = useState(false);
  
  // DEBUG: Track storage events to verify writes (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const handleStorageEvent = (e) => {
        devLog('[SYNC EVENT]', e.detail.key, 'value length:', JSON.stringify(e.detail.value).length);
      };
      window.addEventListener('sr:storage:set', handleStorageEvent);
      return () => window.removeEventListener('sr:storage:set', handleStorageEvent);
    }
  }, []);
  const [equipmentImage, setEquipmentImage] = useState(null);
  const [showCompleteForm, setShowCompleteForm] = useState(true);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    inspector: '',
    allUnitsChecked: true,
    defectsFound: false,
    hasDefects: false,  // Add hasDefects for the form
    notes: '',
    defectDetails: '',
    detailedMode: false,
    equipmentChecks: {},
    selectedEquipmentUnits: []  // Will be populated from selectedEquipmentIds
  });

  // Load job description
  useEffect(() => {
    if (job) {
      // FT-144 FIX: Check BOTH job.description (FT-144 format) and job.jobDescription (legacy)
      // job.description = { purpose, scope } from FT-144 rewrite
      // job.jobDescription = legacy format
      console.log('[FT-144 CheckView] job.description:', JSON.stringify(job?.description));
      console.log('[FT-144 CheckView] job.jobDescription:', JSON.stringify(job?.jobDescription));

      if (job.description && (job.description.purpose || job.description.scope)) {
        // FT-144 format - normalize for rendering
        // FT-147b FIX: DON'T copy purpose to description - keep them separate
        setJobDescription({
          ...job.description,
          // Only use description if it exists AND is different from purpose (avoid duplication)
          description: (job.description.description && job.description.description !== job.description.purpose)
            ? job.description.description
            : '',
          purpose: job.description.purpose || '',
          scope: job.description.scope || ''
        });
      } else if (job.jobDescription) {
        setJobDescription(job.jobDescription);
      } else {
        // Fall back to the separate lookup
        const description = getJobDescription(job.equipmentType);
        setJobDescription(description);
      }
      // Check localStorage for custom image
      const customImage = localStorage.getItem(`equipment_image_${job.id}`);
      setEquipmentImage(customImage || getDefaultImage(job));
    }
  }, [job]);

  // Safe pass-through: MaintenanceCheckView should never crash even if job.linkedEquipmentIds is undefined, [], or missing
  // Pass all equipment to EquipmentStatusTable and let it handle filtering
  const allEquipment = Array.isArray(equipment) ? equipment : [];
  const resolvedEquipment = useMemo(() => resolveJobEquipment(job, allEquipment), [job, allEquipment]);
  
  // For backward compatibility with form logic that expects relevantEquipment
  const relevantEquipment = resolvedEquipment;


  // Initialize equipment checks
  useEffect(() => {
    if (relevantEquipment.length > 0 && formData.detailedMode) {
      const checks = {};
      relevantEquipment.forEach(item => {
        checks[item.id] = {
          checked: false,
          status: 'ok',
          notes: ''
        };
      });
      setFormData(prev => ({ ...prev, equipmentChecks: checks }));
    }
  }, [formData.detailedMode, relevantEquipment]);

  if (!job) return null;

  const handleSubmit = () => {
    if (!formData.inspector) {
      setInspectorError('Inspector name is required (e.g., "John Smith")');
      return;
    }
    setInspectorError('');

    // Don't add completedAt - facade will set it per Anchor Mode
    // FT-349b: include photos + completionId so the completion record owns its
    // evidence (see FT-349a — MaintenanceModule threads these through to
    // completeMaintenanceJob → addCompletedCheck).
    onSave({
      ...formData,
      defectsFound: formData.hasDefects,  // Sync hasDefects to defectsFound
      jobId: job.id,
      selectedEquipmentIds: selectedEquipmentIds,  // NEW: Pass selected equipment IDs
      photos,             // FT-349b
      completionId,       // FT-349b
      signOffNow          // FT-397: optional immediate sign-off
    });

    // FT-349c: draft is now redundant (data lives in the committed completion).
    // Delete the draft key only — DO NOT cascade-delete photos, they belong to
    // the just-saved completion.
    discardDraft();

    // Reset form
    setFormData({
      date: new Date().toISOString().split('T')[0],
      inspector: '',
      allUnitsChecked: true,
      defectsFound: false,
      hasDefects: false,
      notes: '',
      defectDetails: '',
      detailedMode: false,
      equipmentChecks: {},
      selectedEquipmentUnits: []
    });

    setSelectedEquipmentIds([]); // Clear equipment selection
    setShowCompleteForm(true);
    setPhotos([]);
    setCompletionId(uuidv4());
    setSignOffNow(false);
  };

  // FT-349b: Photos are managed in local per-completion state (setPhotos).
  // We keep an `onUpdateJobPhotos`-aware wrapper so the FT-346 test harness
  // (which asserts the parent-callback route) still passes — in production,
  // MaintenanceModule no longer supplies onUpdateJobPhotos and photos live
  // purely in check-view state.
  const handlePhotosChange = useCallback((nextPhotos) => {
    setPhotos(nextPhotos);
    if (typeof onUpdateJobPhotos === 'function') {
      onUpdateJobPhotos(nextPhotos);
    }
  }, [onUpdateJobPhotos]);

  const canShowPhotos = Boolean(currentVesselId);

  // FT-349c: detect whether the user has any unsaved work.
  const hasContent = useMemo(() => {
    if (photos && photos.length > 0) return true;
    return Object.entries(formData || {}).some(([k, v]) => {
      // Skip the date default + non-user fields.
      if (k === 'date' || k === 'detailedMode' || k === 'equipmentChecks' ||
          k === 'selectedEquipmentUnits' || k === 'allUnitsChecked' ||
          k === 'defectsFound' || k === 'hasDefects') return false;
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      return false;
    });
  }, [photos, formData]);

  const clearCompletionState = useCallback(() => {
    setShowCompleteForm(true);
    setSelectedEquipmentIds([]);
    setPhotos([]);
    setCompletionId(uuidv4());
    setSignOffNow(false);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      inspector: '',
      allUnitsChecked: true,
      defectsFound: false,
      hasDefects: false,
      notes: '',
      defectDetails: '',
      detailedMode: false,
      equipmentChecks: {},
      selectedEquipmentUnits: []
    });
  }, []);

  // FT-349c: Cancel path. If there's unsaved content (photos or form fields),
  // show a confirm modal so we can cascade-delete photo blobs on Discard.
  const handleCancelCompletion = useCallback(() => {
    if (hasContent) {
      setShowCancelConfirm(true);
      return;
    }
    clearCompletionState();
  }, [hasContent, clearCompletionState]);

  const handleConfirmCancel = useCallback(async () => {
    await purgeDraft();
    clearCompletionState();
    setShowCancelConfirm(false);
  }, [purgeDraft, clearCompletionState]);

  // FT-349c: rehydrate prompt — show only when a non-stale draft is present
  // and we haven't already asked this mount. Silent auto-apply is tempting
  // but risky (user may not remember starting a check); prompt is safer.
  useEffect(() => {
    if (draftLoading || resumePromptHandled) return;
    if (!draft || !draft.savedAt) return;
    if (Date.now() - draft.savedAt >= DRAFT_STALE_MS) return;
    setShowResumePrompt(true);
  }, [draft, draftLoading, resumePromptHandled]);

  const handleResumeDraft = useCallback(() => {
    if (!draft) return;
    if (draft.form) setFormData((prev) => ({ ...prev, ...draft.form }));
    if (Array.isArray(draft.photos)) setPhotos(draft.photos);
    if (draft.completionId) setCompletionId(draft.completionId);
    // FT-503: form is always shown directly — showCompleteForm stays true on resume.
    setShowCompleteForm(true);
    setShowResumePrompt(false);
    setResumePromptHandled(true);
  }, [draft]);

  const handleDiscardDraft = useCallback(async () => {
    await purgeDraft();
    setCompletionId(uuidv4());
    setPhotos([]);
    setShowResumePrompt(false);
    setResumePromptHandled(true);
  }, [purgeDraft]);

  // FT-349c: autosave. Debounced inside the hook (500ms). Don't fire on empty
  // drafts — first real change triggers the first save.
  // FT-351: also persist which sub-view the user is on so Resume restores it.
  useEffect(() => {
    if (draftLoading) return;
    if (!hasContent) return;
    const viewState = showCompleteForm ? VIEW_STATE_FORM_OPEN : VIEW_STATE_PHOTOS;
    saveDraft(formData, photos, completionId, viewState);
  }, [formData, photos, completionId, hasContent, draftLoading, saveDraft, showCompleteForm]);

  const status = getStatusDisplay(job);

  // Get job history with normalized filtering
  // Pass jobId for v2 to prevent history from appearing in all jobs
  const jobHistory = filterJobHistory(maintenanceHistory, job.equipmentType, job.id);
  devLog('[MaintenanceCheckView] Filtered to:', jobHistory.length, 'items for job:', job.id);

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      {/* Swipe Indicator */}
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Compact Header with Title — equipment image is now display-only
          per FT-346 (broken inline compressImage path removed). */}
      <MaintenanceCheckHeader
        job={job}
        equipmentImage={equipmentImage}
        status={status}
        onBack={onBack}
        formatDate={formatDate}
        onEdit={onEdit}
        isCustomJob={isCustomJob}
      />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="space-y-6">

          {/* Job Description Card - extracted to separate component */}
          <JobDescriptionCard job={job} jobDescription={jobDescription} />

          {/* FT-346: Evidence Photos — new photo evidence pipeline (FT-340) */}
          {canShowPhotos && (
            <section
              className="bg-white rounded-2xl shadow-sm border border-gray-200"
              data-testid="maintenance-photos-section"
            >
              <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-white" />
                  <h2 className="text-base font-semibold text-white">Evidence Photos</h2>
                </div>
              </div>
              <div className="px-6 py-5">
                <p className="mb-3 text-xs text-slate-500">
                  Capture photos as evidence of this maintenance check. Photos sync across your devices.
                </p>
                <PhotoAttachment
                  recordType="maintenance_job"
                  recordId={completionId}
                  vesselId={currentVesselId}
                  capturedBy={capturedBy}
                  photos={photos}
                  onChange={handlePhotosChange}
                  maxCount={20}
                  caption={true}
                  emptyLabel="No photos yet. Add photos to record job evidence."
                  testId="maintenance-job-photos"
                />
              </div>
            </section>
          )}

          {/* Equipment Status Table - now with selectable equipment items */}
          <EquipmentStatusTable
            job={job}
            equipment={resolvedEquipment}
            selectedItems={selectedEquipmentIds}
            onSelectionChange={setSelectedEquipmentIds}
            maintenanceHistory={maintenanceHistory}
          />

          {/* Complete Check Form */}
          <form onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="bg-slate-700 px-5 py-3">
                <h2 className="text-base font-semibold text-white">Complete Check</h2>
              </div>
              <div className="px-6 py-5 space-y-6">
                <MaintenanceCheckForm
                  formData={formData}
                  setFormData={setFormData}
                  relevantEquipment={relevantEquipment}
                  onShowAddEquipment={() => setShowAddEquipment(true)}
                  job={job}
                  crewList={crewList}
                  inspectorError={inspectorError}
                  onInspectorChange={() => setInspectorError('')}
                />
                {/* Inline error messages */}
                {inspectorError && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    {inspectorError}
                  </p>
                )}
              </div>
              <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex items-center justify-between">
                <div>
                  {canSignOff && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={signOffNow}
                        onChange={e => setSignOffNow(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      Sign off now
                    </label>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelCompletion}
                    disabled={isCompleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCompleting}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCompleting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isCompleting ? 'Saving...' : 'Complete Check'}
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Delete button - only for custom jobs */}
          {isCustomJob && onDelete && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {isDeleting ? 'Deleting...' : 'Delete Job'}
              </button>
            </div>
          )}

          {/* FT-397: pending sign-off banner */}
          {canSignOff && jobHistory.some(r => r.signedOffStatus === 'pending') && (
            <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              Some completions are awaiting your sign-off. Tick "Sign off now" when completing the next check.
            </div>
          )}

          {/* Safety Check History */}
          <MaintenanceHistoryTable
            history={jobHistory}
            historySearch={historySearch}
            setHistorySearch={setHistorySearch}
            formatDate={formatDate}
            equipment={relevantEquipment}
          />
        </div>
      </div>

      {/* Quick Add Equipment Modal */}
      <QuickAddEquipmentModal
        isOpen={showAddEquipment}
        onClose={() => setShowAddEquipment(false)}
        equipmentType={
          job?.equipmentType?.toLowerCase().includes('fire extinguisher') ? 'fire_extinguisher' :
          job?.equipmentType?.toLowerCase().includes('lifejacket') ? 'lifejacket' :
          job?.equipmentType?.toLowerCase().includes('liferaft') ? 'liferaft' :
          ''
        }
        jobCategory={job?.category}
        onSave={(newEquipment) => {
          if (onAddEquipment) {
            onAddEquipment(newEquipment);
          }
          setShowAddEquipment(false);
          window.location.reload();
        }}
      />

      {/* FT-349c: Resume-draft prompt */}
      {showResumePrompt && draft && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          data-testid="maintenance-draft-resume-modal"
        >
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Resume draft check?</h3>
            <p className="text-sm text-gray-600 mb-6">
              You have a check in progress from{' '}
              {new Date(draft.savedAt).toLocaleString('en-GB')}. Resume where you left off, or discard the draft to start fresh.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleResumeDraft}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
              >
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FT-349c: Cancel-with-content confirm */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          data-testid="maintenance-cancel-confirm-modal"
        >
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Discard unsaved work?</h3>
            <p className="text-sm text-gray-600 mb-6">
              You have unsaved photos and form data. Discard the check and cascade-delete the photos?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Custom Job?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{getJobDisplayName(job)}</strong>"?
              This action cannot be undone. Any history for this job will be preserved.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onDelete(job.id);  // FT-182 fix: Pass job.id, not job object - archiveJob expects string ID
                  setShowDeleteConfirm(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Delete Job
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceCheckView;
